import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core'; 
import { Router } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule, Location } from '@angular/common';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core'; 
import { Subscription } from 'rxjs'; 

import { AuthService } from '../../core/services/auth.service'; 
import { ChangePasswordPayload, ChangePasswordResponse } from '../../core/models/auth.model';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule
  ],
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.css']
})
export class ChangePasswordComponent implements OnInit, OnDestroy {

  passwordData: ChangePasswordPayload = {
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  };

  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  isLoading = false;
  apiSuccessMessage: string | null = null;
  apiErrorMessage: string | null = null;  

  @ViewChild('passwordForm') passwordForm!: NgForm;

  passwordsDoNotMatchError = false;
  private langChangeSubscription: Subscription | undefined;


  constructor(
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService,
    private location: Location 
  ) {}

  ngOnInit(): void {

  }

  ngOnDestroy(): void {

    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }


  toggleShowCurrentPassword(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }


  toggleShowNewPassword(): void {
    this.showNewPassword = !this.showNewPassword;
  }


  toggleShowConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  checkPasswordsMatch(): void {
    if (this.passwordData.new_password && this.passwordData.new_password_confirmation) {
      this.passwordsDoNotMatchError = this.passwordData.new_password !== this.passwordData.new_password_confirmation;
    } else {
      this.passwordsDoNotMatchError = false;
    }
  }

  onSubmitPasswordChange(): void {
    this.isLoading = true;
    this.apiSuccessMessage = null;
    this.apiErrorMessage = null;

    if (this.passwordForm.invalid) {
      this.apiErrorMessage = this.translate.instant('VALIDATION.FORM_INVALID');
      this.isLoading = false;
      Object.keys(this.passwordForm.controls).forEach(key => {
        this.passwordForm.controls[key].markAsTouched();
      });
      return;
    }

    if (this.passwordData.new_password !== this.passwordData.new_password_confirmation) {
      this.apiErrorMessage = this.translate.instant('VALIDATION.PASSWORDS_DO_NOT_MATCH');
      this.isLoading = false;
      return;
    }

    if (this.passwordData.new_password.length < 6) {
       this.apiErrorMessage = this.translate.instant('VALIDATION.PASSWORD_MIN_LENGTH', { min: 6 });
       this.isLoading = false;
       return;
    }

    const payload: ChangePasswordPayload = this.passwordData;

    this.authService.changePassword(payload).subscribe({
      next: (response: ChangePasswordResponse) => {
        this.isLoading = false;
        if (response.status === 'ok') {
          this.apiSuccessMessage = response.message || this.translate.instant('CHANGE_PASSWORD_PAGE.SUCCESS_MESSAGE');
          
          if (this.passwordForm) {
            this.passwordForm.resetForm({
              current_password: '',
              new_password: '',
              new_password_confirmation: ''
            });
          } else {
             this.passwordData = { current_password: '', new_password: '', new_password_confirmation: '' };
          }
          console.log('Password changed successfully. Logging out and redirecting to login...');
          this.authService.logout().subscribe({
            next: () => {
              console.log('Logout process complete after password change.');
              this.navigateToLoginAfterDelay();
            },
            error: (logoutError) => {
              console.warn('Logout API call after password change failed, but local data cleared. Navigating to login.', logoutError);
              this.navigateToLoginAfterDelay();
            }
          });

        } else {
          if (response.errors) {
            const errorMessages: string[] = [];
            const errorKeys = Object.keys(response.errors) as Array<keyof NonNullable<ChangePasswordResponse['errors']>>;
            errorKeys.forEach(key => {
              const messages = response.errors?.[key];
              if (messages && Array.isArray(messages)) {
                errorMessages.push(...messages);
              }
            });
            this.apiErrorMessage = errorMessages.length > 0 ? errorMessages.join(' ') : (response.message || response.error || this.translate.instant('CHANGE_PASSWORD_PAGE.UNKNOWN_ERROR'));
          } else if (response.message) {
            this.apiErrorMessage = response.message;
          } else if (response.error) {
             this.apiErrorMessage = response.error;
          } else {
            this.apiErrorMessage = this.translate.instant('CHANGE_PASSWORD_PAGE.UNKNOWN_ERROR');
          }
        }
      },
      error: (errorResponse) => {
        this.isLoading = false;
        console.error('Change password HTTP error:', errorResponse);
        if (errorResponse.error && typeof errorResponse.error.message === 'string') {
            this.apiErrorMessage = errorResponse.error.message;
        } else if (typeof errorResponse.message === 'string') {
            this.apiErrorMessage = errorResponse.message;
        } else {
            this.apiErrorMessage = this.translate.instant('CHANGE_PASSWORD_PAGE.REQUEST_FAILED');
        }
      }
    });
  }

  private navigateToLoginAfterDelay(): void {
    setTimeout(() => {
      this.router.navigate(['/login']);
    }, 1000); 
  }

  goBack(): void {
    this.location.back();
  }
}